import { useZero } from "@rocicorp/zero/react";
import { keyframes, styled } from "goober";
import { useState } from "react";
import {
  connectorsByIdentifier,
  connectorTypeOf,
} from "../../shared/connectors";
import {
  gitAuthIdentifiers,
  supportsGitAuth,
  supportsManualGitWebhook,
} from "../../shared/connectors/gitAuthIdentifiers";
import { sharedMutators } from "../../shared/mutators";
import { GIT_SYNC_STALE_MS } from "../../shared/mutators/syncGit";
import { queries } from "../../shared/queries";
import { isSupplierAvailableFromGrants } from "../../shared/rbac/crossResourceAvailability";
import { useConnectorCreator } from "../hooks/useConnectorCreator";
import { useGroupMemberships } from "../hooks/useGroupMemberships";
import { useQuery } from "../hooks/useQuery";
import { formatRelativeTime } from "../lib/formatRelativeTime";
import { apiUrl } from "../lib/tenantOrigin";
import { makeUrl, routes } from "../lib/urls";
import { Button } from "./Button";
import { ConfirmModal } from "./ConfirmModal";
import { ConnectorLogo } from "./ConnectorLogo";
import { ConnectorSelect } from "./ConnectorSelect";
import { Icon } from "./Icon";
import { Input } from "./Input";
import { IntegrationButton } from "./IntegrationButton";
import { Link } from "./Link";
import { SettingsCard as Card } from "./SettingsCard";
import { CopyableValue } from "./Settings/CopyableValue";
import { Text } from "./Text";
import { Toggle } from "./Toggle";

const Header = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 0 8px;
`;

const HeaderText = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const RemoteBox = styled("div")`
  background: var(--surface-primary);
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
`;

const RemoteIcon = styled("div")`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--alpha4);
  color: var(--text-primary);
`;

const RemoteInfo = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const RepoLink = styled("a")`
  display: block;
  min-width: 0;
  align-self: flex-start;
  max-width: 100%;
  color: var(--text-primary);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const Field = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 8px;
`;

const spin = keyframes`
  to {
    transform: rotate(-360deg);
  }
`;

const SyncButton = styled(Button)`
  &.syncing span {
    animation: ${spin} 2s linear infinite;
  }
`;

// One entry per product, keeping the first (default) auth variant; the
// connector modal's variant picker offers the product's other auth methods.
const gitProviders = gitAuthIdentifiers
  .map((identifier) => connectorsByIdentifier[identifier]!)
  .filter(
    (definition, index, all) =>
      index ===
      all.findIndex(
        (d) =>
          connectorTypeOf(d.identifier)?.identifier ===
          connectorTypeOf(definition.identifier)?.identifier
      )
  );

function repoNameFromUrl(remoteUrl: string): string {
  const path = new URL(remoteUrl).pathname
    .replace(/^\/+/, "")
    .replace(/\.git$/, "");
  return path || remoteUrl;
}

type Props = {
  spaceId: string;
  canManageSpace: boolean;
};

export function GitSyncForm({ spaceId, canManageSpace }: Props) {
  const z = useZero();
  const [gitSync] = useQuery(queries.loadSpaceGitRemote({ spaceId }));
  const [allApps] = useQuery(queries.connectedAppsIndex());
  const [spaceGrants] = useQuery(
    queries.roleGrantsByScope({ scopeId: spaceId })
  );
  const { userGroupIds: userGroupMemberships } = useGroupMemberships();
  const [formOpen, setFormOpen] = useState(false);
  const [repoUrlValue, setRepoUrlValue] = useState("");
  const [selectedAppId, setSelectedAppId] = useState("");
  const [confirmDisconnect, setConfirmDisconnect] = useState<
    false | "toggle" | "row"
  >(false);
  const createConnector = useConnectorCreator(setSelectedAppId, spaceId);

  // syncStartedAt is set by the syncGit mutation and cleared when the
  // server-side task settles, so every client sees the same in-progress state.
  const isSyncing =
    gitSync?.syncStartedAt != null &&
    Date.now() - gitSync.syncStartedAt < GIT_SYNC_STALE_MS;

  const hasRemote = Boolean(
    gitSync?.remoteUrl && gitSync?.appUsage?.connectedAppId
  );
  const remoteApp = gitSync?.appUsage?.connectedApp;
  const gitApps =
    spaceGrants.length === 0
      ? []
      : allApps.filter(
          (app) =>
            supportsGitAuth(app.identifier) &&
            isSupplierAvailableFromGrants(
              "space_uses_connected_apps",
              spaceGrants,
              app.roleGrantsAsScope,
              userGroupMemberships
            )
        );

  const handleConfigure = async () => {
    if (!repoUrlValue.trim() || !selectedAppId) return;
    await z.mutate(
      sharedMutators.configureGitSync({
        spaceId,
        remoteUrl: repoUrlValue.trim(),
        connectedAppId: selectedAppId,
      })
    ).client;
    setRepoUrlValue("");
    setSelectedAppId("");
    setFormOpen(false);
  };

  const handleDisconnect = async () => {
    const reopenForm = confirmDisconnect === "row";
    await z.mutate(sharedMutators.disconnectGitSync({ spaceId })).client;
    setConfirmDisconnect(false);
    setFormOpen(reopenForm);
  };

  const handleSync = async () => {
    await z.mutate(sharedMutators.syncGit({ spaceId, action: "sync" })).client;
  };

  return (
    <>
      <Card>
        <Header>
          <HeaderText>
            <Text variant="text-regular-md">Git sync</Text>
            <Text variant="text-small" color="var(--text-secondary)">
              Sync workflows to a git repository
            </Text>
          </HeaderText>
          {canManageSpace && (
            <Toggle
              isOn={hasRemote || formOpen}
              onToggle={() => {
                if (hasRemote) {
                  setConfirmDisconnect("toggle");
                } else if (formOpen) {
                  setFormOpen(false);
                  setRepoUrlValue("");
                  setSelectedAppId("");
                } else {
                  setFormOpen(true);
                }
              }}
            />
          )}
        </Header>

        {hasRemote ? (
          <>
            <RemoteBox>
              <RemoteIcon>
                {remoteApp ? (
                  <ConnectorLogo
                    template={{
                      name: remoteApp.name,
                      identifier: remoteApp.identifier,
                    }}
                    size={16}
                  />
                ) : (
                  <Icon name="git-branch" size={16} />
                )}
              </RemoteIcon>
              <RemoteInfo>
                <RepoLink
                  href={gitSync!.remoteUrl.replace(/\.git$/, "")}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={gitSync!.remoteUrl}
                >
                  <Text
                    ellipsis
                    variant="text-regular-md"
                    color="var(--text-primary)"
                  >
                    {repoNameFromUrl(gitSync!.remoteUrl)}
                  </Text>
                </RepoLink>
                <Text variant="text-small" color="var(--text-secondary)">
                  {remoteApp && (
                    <>
                      {"Via "}
                      <Link
                        to={makeUrl(routes.modal.connector, {
                          id: remoteApp.id,
                        })}
                        style={{
                          color: "inherit",
                          textDecoration: "underline",
                        }}
                      >
                        {remoteApp.name}
                      </Link>
                      {" · "}
                    </>
                  )}
                  {isSyncing
                    ? "Syncing…"
                    : gitSync!.lastSyncedAt
                      ? `Last synced ${formatRelativeTime(gitSync!.lastSyncedAt)}`
                      : "Not synced yet"}
                </Text>
              </RemoteInfo>
              {canManageSpace && (
                <>
                  <SyncButton
                    className={isSyncing ? "syncing" : undefined}
                    variant="secondary"
                    icon="refresh-ccw"
                    onClick={handleSync}
                    disabled={isSyncing}
                    aria-label="Sync"
                    title="Sync"
                  />
                  <Button
                    variant="secondary"
                    icon="circle-minus"
                    onClick={() => setConfirmDisconnect("row")}
                    aria-label="Disconnect"
                    title="Disconnect"
                  />
                </>
              )}
            </RemoteBox>
            {!isSyncing && gitSync!.lastError && (
              <Text
                variant="text-small"
                color="var(--red-600)"
                style={{ padding: "0 8px" }}
              >
                {gitSync!.lastError}
              </Text>
            )}
            {canManageSpace &&
              remoteApp &&
              supportsManualGitWebhook(remoteApp.identifier) && (
                <WebhookSetup app={remoteApp} gitSync={gitSync!} />
              )}
          </>
        ) : formOpen ? (
          gitApps.length > 0 ? (
            <>
              <Field>
                <Text variant="text-small-md">Connector</Text>
                <ConnectorSelect
                  apps={gitApps}
                  value={selectedAppId || null}
                  selectedName={
                    gitApps.find((a) => a.id === selectedAppId)?.name ?? null
                  }
                  onChange={(id) => setSelectedAppId(id ?? "")}
                  placeholder="Select a connection"
                  addNew={gitProviders.map((provider) => ({
                    label: provider.name,
                    subtitle: provider.authLabel,
                    template: {
                      name: provider.name,
                      identifier: provider.identifier,
                    },
                    onClick: () => createConnector(provider.identifier),
                  }))}
                />
              </Field>
              <Field>
                <Text variant="text-small-md">Repository URL</Text>
                <Input
                  placeholder="https://github.com/org/repo.git"
                  value={repoUrlValue}
                  onChange={(e) => setRepoUrlValue(e.target.value)}
                  style={{ width: "100%" }}
                />
              </Field>
              {selectedAppId && repoUrlValue.trim() && (
                <div style={{ padding: "0 8px" }}>
                  <Button variant="primary" onClick={handleConfigure}>
                    Connect
                  </Button>
                </div>
              )}
            </>
          ) : (
            <Field>
              <Text variant="text-small" color="var(--text-secondary)">
                To set up git sync, first connect a git provider.
              </Text>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {gitProviders.map((provider) => (
                  <IntegrationButton
                    key={provider.identifier}
                    type="button"
                    logo={provider.logo}
                    text={`Connect ${provider.name}`}
                    onClick={() => createConnector(provider.identifier)}
                  />
                ))}
              </div>
            </Field>
          )
        ) : null}
      </Card>

      <ConfirmModal
        open={confirmDisconnect !== false}
        onClose={() => setConfirmDisconnect(false)}
        onConfirm={handleDisconnect}
        title="Disconnect git sync"
        description="This will remove the git sync configuration for this space. Workflows will not be deleted."
        confirmLabel="Disconnect"
        destructive
      />
    </>
  );
}

function WebhookSetup({
  app,
  gitSync,
}: {
  app: { id: string; configuration: Record<string, string> };
  gitSync: { remoteUrl: string; webhookReceivedAt: number | null };
}) {
  const z = useZero();
  const [token, setToken] = useState("");
  const [reconfiguring, setReconfiguring] = useState(false);
  const webhookUrl = `${apiUrl()}/connectors/gitlab/webhook/${app.id}`;
  const hooksSettingsUrl = `${gitSync.remoteUrl.replace(/\.git$/, "")}/-/hooks`;
  const configured = Boolean(app.configuration.webhookSecretSetAt);

  const handleSave = async () => {
    await z.mutate(
      sharedMutators.setConnectorWebhookSecret({
        connectorId: app.id,
        secret: token.trim(),
      })
    ).server;
    setToken("");
    setReconfiguring(false);
  };

  if (gitSync.webhookReceivedAt != null && !reconfiguring) {
    return (
      <Field>
        <Text variant="text-regular-md">Webhooks</Text>
        <Text variant="text-small" color="var(--text-secondary)">
          Connected. GitLab delivered a webhook{" "}
          {formatRelativeTime(gitSync.webhookReceivedAt)}.
        </Text>
        <div>
          <Button variant="secondary" onClick={() => setReconfiguring(true)}>
            Reconfigure
          </Button>
        </div>
      </Field>
    );
  }

  return (
    <Field>
      <Text variant="text-regular-md">Webhooks</Text>
      <Text variant="text-small" color="var(--text-secondary)">
        Webhooks let GitLab tell 3B about pushes and merge requests, so syncs
        and merge request links happen automatically. In your GitLab project,
        open{" "}
        <a
          href={hooksSettingsUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "inherit", textDecoration: "underline" }}
        >
          {"Settings > Webhooks"}
        </a>
        , select Add new webhook, and paste this URL. Under Trigger, check
        exactly two boxes: Push events (all branches) and Merge request events.
      </Text>
      <Text variant="text-small-md">Webhook URL</Text>
      <CopyableValue value={webhookUrl}>
        <Text variant="text-small" ellipsis>
          {webhookUrl}
        </Text>
      </CopyableValue>
      <Text variant="text-small-md">Signing token</Text>
      <Text variant="text-small" color="var(--text-secondary)">
        In the GitLab webhook form, select Generate signing token and paste it
        here. On older GitLab versions without signing tokens, enter a secret of
        your own and use the same value as GitLab’s secret token.
      </Text>
      <Input
        type="password"
        placeholder="whsec_…"
        value={token}
        onChange={(e) => setToken(e.target.value)}
        style={{ width: "100%" }}
      />
      {configured && !token && (
        <Text variant="text-small" color="var(--text-secondary)">
          Token saved, waiting for the first delivery. Push to the repository or
          select Test in the GitLab webhook form to check the connection.
        </Text>
      )}
      {token.length > 0 && (
        <div>
          <Button
            variant="secondary"
            onClick={handleSave}
            disabled={token.trim().length < 16}
            title={
              token.trim().length < 16
                ? "Tokens are at least 16 characters"
                : undefined
            }
          >
            Save token
          </Button>
        </div>
      )}
    </Field>
  );
}
